import cn from 'classnames';
import { ToolTip } from 'components/Shared/ToolTip';
import { InputProps, Input } from 'components/Shared/Form/input';
import { MutableRefObject, useRef } from 'react';
import { isBrowser, NO_ELEMENT, rm } from 'utils';
import { CHAR_SIZE } from 'components/Shared/Form/inputLabelConfig/themes/BGH';
import styles from './styles.scss';

interface Props extends InputProps {
  preLabel?: string;
  label?: string;
  tooltip?: string;
  onClear?: () => void;
}

export const LabeledInput = ({
  className,
  preLabel = rm('checkout_prelabel'),
  label,
  placeholder,
  tooltip,
  unitClassName,
  innerRef,
  onClear,
  ...props
}: Props) => {
  const preLabelRef = useRef<HTMLSpanElement>(NO_ELEMENT);
  const ownInputRef = useRef<HTMLInputElement>();
  const inputRef = innerRef ?? ownInputRef;
  const hasValue = !!props.value;
  const isValid = typeof props.valid === 'boolean' ? props.valid : hasValue;

  const inputIsFocused = isBrowser() && document.activeElement === inputRef.current;
  const hidePreLabel = hasValue || inputIsFocused;

  const preLabelWidth = preLabel.length * CHAR_SIZE;
  const getPreLabelStyle = () => ({
    width: hidePreLabel ? 0 : preLabelWidth,
    overflow: hidePreLabel ? 'hidden' : 'initial'
  });

  return (
    <div className={styles.container}>
      <div
        className={cn(
          styles.labelInput,
          {
            [styles.valid]: isValid,
            [styles.invalid]: props.invalid
          },
          className
        )}
      >
        <Input
          innerRef={inputRef as MutableRefObject<HTMLInputElement>}
          unitClassName={cn(styles.unit, unitClassName)}
          onClear={onClear}
          {...props}
        />

        {label && (
          <label className={cn(styles.labelWrapper, { [styles.withValue]: hidePreLabel })}>
            <span ref={preLabelRef} className={styles.preLabel} style={getPreLabelStyle()}>
              {preLabel}
            </span>
            <span
              className={cn(styles.label, {
                [styles.lower]: !hidePreLabel,
                [styles.upper]: hidePreLabel
              })}
            >
              {fixLabel(preLabel, hidePreLabel ? placeholder || label : label)}
            </span>
          </label>
        )}
      </div>

      {tooltip && <ToolTip className={styles.tooltip} text={tooltip} />}
    </div>
  );
};

function fixLabel(preLabel: string, label = '') {
  return label.replace(preLabel, '');
}
